import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTranslations } from '../context/TranslationsContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Calendar, Eye, ArrowLeft, Tag, Share2, Clock, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTH_KEYS_FULL = ['january','february','march','april','may_full','june','july','august','september','october','november','december'];

// Helper to safely extract string from multilang field
const getLocalizedString = (field, lang, fallback = '') => {
  if (!field) return fallback;
  if (typeof field === 'string') return field;
  return field[lang] || field.pl || field.en || Object.values(field)[0] || fallback;
};

// Skeleton loader
const PostSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-8 bg-white/10 rounded w-3/4 mb-4" />
    <div className="flex gap-4 mb-8">
      <div className="h-4 bg-white/10 rounded w-24" />
      <div className="h-4 bg-white/10 rounded w-24" />
    </div>
    <div className="aspect-video bg-white/10 rounded-xl mb-8" />
    <div className="space-y-4">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="h-4 bg-white/10 rounded" style={{ width: `${80 + Math.random() * 20}%` }} />
      ))}
    </div>
  </div>
);

// Related post card
const RelatedPostCard = ({ post, lang }) => (
  <Link to={`/${lang}/blog/${post.slug}`} className="group">
    <Card className="bg-white/5 border-white/10 overflow-hidden hover:border-primary/50 transition-all">
      <div className="aspect-video overflow-hidden bg-white/5">
        {post.featuredImage ? (
          <img
            src={post.featuredImage}
            alt={getLocalizedString(post.title, lang)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-purple-600/20">
            <span className="text-2xl font-bold text-white/20">
              {getLocalizedString(post.title, lang, 'A')[0]}
            </span>
          </div>
        )}
      </div>
      <CardContent className="p-4">
        <h4 className="text-sm font-medium text-white line-clamp-2 group-hover:text-primary transition-colors">
          {getLocalizedString(post.title, lang)}
        </h4>
      </CardContent>
    </Card>
  </Link>
);

const BlogPostPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { tr, td } = useTranslations();
  const lang = i18n.language;
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Localized string helper bound to current lang
  const ls = (field, fallback = '') => getLocalizedString(field, lang, fallback);

  // Calculate SEO values
  const pageTitle = ls(post?.title, 'AvePark Blog');
  const seoTitle = ls(post?.seoTitle) || pageTitle;
  const seoDescription = ls(post?.seoDescription) || ls(post?.excerpt) || '';

  const t = (key, fallback) => {
    return tr(key, ti18n(key, fallback || key));
  };

  useEffect(() => {
    fetchPost();
  }, [slug]);

  // Set document title
  useEffect(() => {
    document.title = seoTitle ? `${seoTitle} | AvePark Blog` : 'AvePark Blog';
    return () => {
      document.title = 'AvePark';
    };
  }, [seoTitle]);

  const fetchPost = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [postRes, relatedRes] = await Promise.all([
        axios.get(`${API}/api/blog/posts/${slug}`),
        axios.get(`${API}/api/blog/posts/${slug}/related`)
      ]);
      
      setPost(postRes.data);
      setRelated(relatedRes.data);
    } catch (err) {
      console.error('Error fetching post:', err);
      if (err.response?.status === 404) {
        setError('not_found');
      } else {
        setError('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate();
    const monthKey = MONTH_KEYS_FULL[date.getMonth()];
    const monthName = td(`months.${monthKey}`) || date.toLocaleDateString('pl-PL', { month: 'long' });
    return `${day} ${monthName} ${date.getFullYear()}`;
  };

  const estimateReadingTime = (contentHtml) => {
    if (!contentHtml) return 1;
    const text = contentHtml.replace(/<[^>]+>/g, '');
    const words = text.split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 200));
  };

  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: pageTitle, url });
      } catch (err) {
        // User cancelled sharing
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success(t('blog.linkCopied', 'Link skopiowany!'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen page-gradient pt-28 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <PostSkeleton />
        </div>
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen page-gradient pt-28 pb-16">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h1 className="text-3xl font-bold text-white mb-4">
            {t('blog.notFound', 'Artykuł nie znaleziony')}
          </h1>
          <p className="text-gray-400 mb-8">
            {t('blog.notFoundDesc', 'Przepraszamy, ten artykuł nie istnieje lub został usunięty.')}
          </p>
          <Link to={`/${lang}/blog`}>
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('blog.backToBlog', 'Wróć do bloga')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen page-gradient pt-28 pb-16">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h1 className="text-3xl font-bold text-white mb-4">
            {t('common.error', 'Wystąpił błąd')}
          </h1>
          <Button onClick={fetchPost}>
            {t('common.tryAgain', 'Spróbuj ponownie')}
          </Button>
        </div>
      </div>
    );
  }

  const content = ls(post?.content);

  return (
    <>
      {/* SEO meta tags */}
      <Helmet>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        {post?.featuredImage && <meta property="og:image" content={post.featuredImage} />}
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="min-h-screen page-gradient pt-28 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumbs */}
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-8">
              <Link to={`/${lang}/blog`} className="hover:text-white transition-colors">
                Blog
              </Link>
              {post?.category && (
                <>
                  <ChevronRight className="w-4 h-4" />
                  <Link 
                    to={`/${lang}/blog/kategoria/${post.category.slug}`}
                    className="hover:text-white transition-colors"
                  >
                    {ls(post.category.name)}
                  </Link>
                </>
              )}
              <ChevronRight className="w-4 h-4" />
              <span className="text-white truncate max-w-[200px]">{pageTitle}</span>
            </nav>

            {/* Back button */}
            <Link to={`/${lang}/blog`} className="inline-flex items-center text-gray-400 hover:text-white transition-colors mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('blog.backToBlog', 'Wróć do bloga')}
            </Link>

            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Category */}
              {post?.category && (
                <Link 
                  to={`/${lang}/blog/kategoria/${post.category.slug}`}
                  className="inline-flex items-center gap-1 text-primary text-sm font-medium uppercase tracking-wider mb-4 hover:text-primary/80"
                >
                  <Tag className="w-3 h-3" />
                  {ls(post.category.name)}
                </Link>
              )}

              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                {pageTitle}
              </h1>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm mb-8">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(post?.publishedAt)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {estimateReadingTime(content)} {t('blog.read.time', 'min')} {t('blog.read', 'czytania')}
                </span>
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  {post?.viewCount || 0} {t('blog.views', 'wyświetleń')}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleShare}
                  className="text-gray-400 hover:text-white"
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  {t('blog.share', 'Udostępnij')}
                </Button>
              </div>

              {/* Featured image */}
              {post?.featuredImage && (
                <div className="aspect-video rounded-xl overflow-hidden mb-8">
                  <img
                    src={post.featuredImage}
                    alt={pageTitle}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Content */}
              <div 
                className="prose prose-invert prose-lg max-w-none mb-12"
                dangerouslySetInnerHTML={{ __html: content }}
              />

              {/* Share at bottom */}
              <div className="border-t border-white/10 pt-8 mb-12">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">
                    {t('blog.enjoyedArticle', 'Podobał Ci się artykuł?')}
                  </span>
                  <Button onClick={handleShare} variant="outline" className="border-white/20">
                    <Share2 className="w-4 h-4 mr-2" />
                    {t('blog.share', 'Udostępnij')}
                  </Button>
                </div>
              </div>
            </motion.article>

            {/* Related posts */}
            {related.length > 0 && (
              <section>
                <h2 className="text-xl font-bold text-white mb-6">
                  {t('blog.relatedPosts', 'Podobne artykuły')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {related.map(relatedPost => (
                    <RelatedPostCard key={relatedPost.id} post={relatedPost} lang={lang} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>

      {/* Article styles */}
      <style>{`
        .prose h1 { font-size: 2em; margin-top: 1.5em; margin-bottom: 0.5em; }
        .prose h2 { font-size: 1.5em; margin-top: 1.25em; margin-bottom: 0.5em; }
        .prose h3 { font-size: 1.25em; margin-top: 1em; margin-bottom: 0.5em; }
        .prose p { margin: 1em 0; line-height: 1.8; }
        .prose ul, .prose ol { padding-left: 1.5em; margin: 1em 0; }
        .prose li { margin: 0.5em 0; }
        .prose blockquote {
          border-left: 3px solid #8b5cf6;
          padding-left: 1em;
          margin: 1.5em 0;
          color: #a1a1aa;
          font-style: italic;
        }
        .prose pre {
          background: #0d0d1a;
          padding: 1em;
          border-radius: 0.5em;
          overflow-x: auto;
          margin: 1.5em 0;
        }
        .prose code {
          background: rgba(139, 92, 246, 0.2);
          padding: 0.2em 0.4em;
          border-radius: 0.25em;
          font-size: 0.9em;
        }
        .prose pre code {
          background: none;
          padding: 0;
        }
        .prose img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5em;
          margin: 1.5em 0;
        }
        .prose a {
          color: #8b5cf6;
          text-decoration: underline;
        }
        .prose a:hover {
          color: #a78bfa;
        }
        .prose hr {
          border: none;
          border-top: 1px solid rgba(255,255,255,0.1);
          margin: 2em 0;
        }
      `}</style>
    </>
  );
};

export default BlogPostPage;
